import React, { useState, useEffect } from 'react';
import { 
  BookOpen, FileText, Download, Target, Layers, CheckCircle2, 
  Globe, Users, Landmark, TrendingUp, Calendar, Printer, List
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from 'sonner';
import { getCurrentUser } from '@/data/users';
import { socialScienceCurriculum, socialScienceSchemeOfWork, socialScienceTeacherResources, socialScienceYearlyPlanner, socialScienceResources } from '@/data/socialScienceCurriculum';
import apiService from '@/services/api';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { FileQuestion, Files, FolderOpen } from 'lucide-react';

export default function SocialScienceCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 9");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const currentUser = getCurrentUser();
  const teacherResources = socialScienceTeacherResources[currentUser?.id] || [];
  
  // Memoize restrictedTo to avoid re-render loops
  const restrictedTo = currentUser?.restrictedTo || currentUser?.restricted_to;

  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        // First check if user has restricted_to field (e.g., "8-9" for Years 8 & 9)
        if (restrictedTo) {
          // Parse restricted_to (e.g., "8-9", "7-10", "9")
          let allowedYears = [];
          if (String(restrictedTo).includes('-')) {
            const [start, end] = String(restrictedTo).split('-').map(Number);
            for (let i = start; i <= end; i++) {
              allowedYears.push(`Year ${i}`);
            }
          } else {
            allowedYears = [`Year ${restrictedTo}`];
          }
          
          setTeacherYears(allowedYears);
          setSelectedYear(allowedYears[0]);
          setLoading(false);
          return;
        }
        
        // Otherwise fetch from classes API
        const classes = await apiService.getTeacherClasses(true);
        const years = [...new Set(classes
          .filter(c => c.subject?.toLowerCase().includes('social'))
          .map(c => `Year ${c.year}`)
        )].sort((a, b) => {
          const yearA = parseInt(a.replace('Year ', ''));
          const yearB = parseInt(b.replace('Year ', ''));
          return yearA - yearB;
        });
        
        setTeacherYears(years.length > 0 ? years : ["Year 8", "Year 9"]);
        if (years.length > 0) {
          setSelectedYear(years[0]);
        }
      } catch (error) {
        console.error('Error fetching teacher classes:', error);
        setTeacherYears(["Year 8", "Year 9"]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, [restrictedTo]);

  const curriculumData = socialScienceCurriculum[selectedYear] || socialScienceCurriculum["Year 9"];
  const schemeData = socialScienceSchemeOfWork[selectedYear] || socialScienceSchemeOfWork["Year 9"];

  const getStrandIcon = (name) => {
    if (name.includes('History')) return <Landmark className="h-5 w-5" />;
    if (name.includes('Geography')) return <Globe className="h-5 w-5" />;
    if (name.includes('Civics')) return <Users className="h-5 w-5" />;
    if (name.includes('Economics')) return <TrendingUp className="h-5 w-5" />;
    return <BookOpen className="h-5 w-5" />;
  };

  return (
    <div className="space-y-6" data-testid="social-science-curriculum-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between gap-4 md:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Globe className="h-8 w-8 text-amber-600" />
            Social Science Curriculum
          </h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive social science curriculum for {selectedYear}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Select Year" />
            </SelectTrigger>
            <SelectContent>
              {teacherYears.map((year) => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Teacher Resources Section - Only shown if resources exist */}
      {teacherResources.length > 0 && (
        <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-600" />
              Your Teaching Resources
            </CardTitle>
            <CardDescription>Official curriculum documents and guides assigned to you</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teacherResources.map((resource) => (
                <div key={resource.id} className="bg-white rounded-lg p-4 border border-amber-200 hover:border-amber-400 transition-colors shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                        <FileText className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{resource.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{resource.description}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">{resource.year}</Badge>
                          <Badge variant="outline" className="text-xs">.{resource.type.toUpperCase()}</Badge>
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="shrink-0"
                      onClick={() => window.open(resource.url, '_blank')}
                      data-testid={`download-${resource.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="curriculum" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Curriculum
          </TabsTrigger>
          <TabsTrigger value="yearly-planner" className="flex items-center gap-2 bg-green-100">
            <Calendar className="h-4 w-4" />
            Yearly Planner
          </TabsTrigger>
          <TabsTrigger value="resources" className="flex items-center gap-2 bg-amber-100">
            <FolderOpen className="h-4 w-4" />
            Resources
          </TabsTrigger>
          <TabsTrigger value="scheme" className="flex items-center gap-2">
            <List className="h-4 w-4" />
            Scheme of Work
          </TabsTrigger>
          <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-blue-100">
            <Files className="h-4 w-4" />
            Quick Print
          </TabsTrigger>
          <TabsTrigger value="assessment" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Assessment
          </TabsTrigger>
        </TabsList>

        {/* Yearly Planner Tab */}
        <TabsContent value="yearly-planner" className="space-y-4">
          {/* Term Selection */}
          <div className="flex flex-wrap gap-2">
            {['term1', 'term2', 'term3'].map((term) => (
              <Button
                key={term}
                variant={selectedTerm === term ? "default" : "outline"}
                onClick={() => setSelectedTerm(term)}
                className={selectedTerm === term ? "bg-amber-600 hover:bg-amber-700" : ""}
              >
                Term {term.replace('term', '')}
              </Button>
            ))}
          </div>

          {/* Term Overview */}
          {socialScienceYearlyPlanner?.[`year${selectedYear.replace('Year ', '')}`]?.[selectedTerm] && (
            <Card>
              <CardHeader className="bg-amber-50 border-b border-amber-100">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-amber-800">Term {selectedTerm.replace('term', '')} Overview</CardTitle>
                    <CardDescription className="text-amber-700">
                      {socialScienceYearlyPlanner[`year${selectedYear.replace('Year ', '')}`][selectedTerm].weeks} • {socialScienceYearlyPlanner[`year${selectedYear.replace('Year ', '')}`][selectedTerm].focus}
                    </CardDescription>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700">
                    {socialScienceYearlyPlanner[`year${selectedYear.replace('Year ', '')}`][selectedTerm].assessment}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  {socialScienceYearlyPlanner[`year${selectedYear.replace('Year ', '')}`][selectedTerm].units?.map((unit, idx) => (
                    <div key={idx} className="p-4 border rounded-lg hover:border-amber-300 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-amber-800">{unit.title}</h4>
                        <Badge variant="outline" className="text-amber-600">{unit.weeks}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        <span className="font-medium">{unit.strand}</span>
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {unit.topics?.map((topic, tIdx) => (
                          <Badge key={tIdx} variant="outline" className="text-xs bg-amber-50">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5" />
                Teaching Resources - {selectedYear}
              </CardTitle>
              <CardDescription>Reference materials and guides for Social Science teaching</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(socialScienceResources?.[`year${selectedYear.replace('Year ', '')}`] || []).map((resource, idx) => (
                  <div key={idx} className="p-4 border rounded-lg hover:border-amber-300 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-amber-800">{resource.title}</h4>
                      <Badge variant="outline" className="text-amber-600 text-xs">{resource.type}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{resource.description}</p>
                    <div className="flex items-center justify-between">
                      <Badge className="bg-gray-100 text-gray-600">{resource.unit}</Badge>
                      <Button size="sm" variant="outline" className="text-amber-600 hover:text-amber-700">
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quick Print Tab */}
        <TabsContent value="quick-print" className="space-y-4">
          <QuickPrintAll
            subject="Social Science"
            year={parseInt(selectedYear.replace('Year ', ''))}
            curriculum={curriculumData}
            schemeOfWork={schemeData}
            subjectColor="amber"
          />
        </TabsContent>

        {/* Curriculum Tab */}
        <TabsContent value="curriculum" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Learning Strands</CardTitle>
              <CardDescription>Core content areas for {selectedYear} Social Science</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {curriculumData?.strands?.map((strand) => (
                  <Card key={strand.id} className="border-2">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-lg ${strand.color.split(' ')[0]}`}>
                          {getStrandIcon(strand.name)}
                        </div>
                        <CardTitle className="text-lg">{strand.name}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {strand.topics.map((topic, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheme of Work Tab */}
        <TabsContent value="scheme" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Scheme of Work</CardTitle>
                  <CardDescription>Weekly teaching plan for {selectedYear}</CardDescription>
                </div>
                <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="term1">Term 1</SelectItem>
                    <SelectItem value="term2">Term 2</SelectItem>
                    <SelectItem value="term3">Term 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                const weeks = schemeData?.[selectedTerm]?.weeks || [];
                const hasSchemes = weeks.some(w => w.scheme);
                return (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[80px]">Week</TableHead>
                        {hasSchemes && <TableHead className="w-[220px]">Scheme</TableHead>}
                        <TableHead>Topic</TableHead>
                        <TableHead>Activities</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {weeks.map((week, idx) => {
                        const showScheme = hasSchemes && (idx === 0 || week.scheme !== weeks[idx - 1]?.scheme);
                        const schemeSpan = hasSchemes ? weeks.filter(w => w.scheme === week.scheme).length : 0;
                        return (
                          <TableRow key={week.week}>
                            <TableCell className="font-medium">Week {week.week}</TableCell>
                            {hasSchemes && showScheme && (
                              <TableCell rowSpan={schemeSpan} className="align-top border-l-4 border-amber-400 bg-amber-50/50 font-medium text-amber-800 text-sm">
                                {week.scheme}
                              </TableCell>
                            )}
                            {hasSchemes && !showScheme && null}
                            <TableCell className="font-medium">{week.topic}</TableCell>
                            <TableCell className="text-muted-foreground">{week.activities}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assessment Tab */}
        <TabsContent value="assessment" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Assessment Weighting</CardTitle>
              <CardDescription>How student performance is evaluated in {selectedYear}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {curriculumData?.assessments?.map((assessment, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{assessment.name}</span>
                      <span className="text-muted-foreground">{assessment.weight}%</span>
                    </div>
                    <Progress value={assessment.weight} className="h-2" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Assessment Guidelines</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">Research Project</h4>
                  <p className="text-sm text-blue-700">Students conduct in-depth research on a social science topic, demonstrating inquiry skills and critical thinking.</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">Class Tests</h4>
                  <p className="text-sm text-green-700">Regular tests covering content from each strand to assess knowledge retention and understanding.</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="font-semibold text-purple-800 mb-2">Assignments</h4>
                  <p className="text-sm text-purple-700">Written assignments including essays, case studies, and analysis of current events.</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2">Final Examination</h4>
                  <p className="text-sm text-amber-700">Comprehensive end-of-year examination covering all strands and topics studied.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
